import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, LogOut } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { AdminLogin } from "../components/admin/AdminLogin";
import { WeeklyEditor } from "../components/admin/WeeklyEditor";
import { BlockedDates } from "../components/admin/BlockedDates";
import { BookingList } from "../components/admin/BookingList";
import { MessageList } from "../components/admin/MessageList";
import { BlogAdmin } from "../components/admin/BlogAdmin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TABS = [
  { id: "bookings", label: "Prenotazioni" },
  { id: "messages", label: "Messaggi" },
  { id: "blog", label: "Blog" },
  { id: "availability", label: "Disponibilità" },
];

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("gyc-admin-token"));
  const [tab, setTab] = useState("bookings");
  const [availability, setAvailability] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [saving, setSaving] = useState(false);

  const logout = useCallback(() => {
    sessionStorage.removeItem("gyc-admin-token");
    setToken(null);
  }, []);

  useEffect(() => {
    if (!token) return;
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    const onErr = (e) => { if (e.response?.status === 401) logout(); };
    axios.get(`${API}/availability`).then((r) => setAvailability(r.data));
    axios.get(`${API}/admin/bookings`, auth).then((r) => setBookings(r.data)).catch(onErr);
    axios.get(`${API}/admin/messages`, auth).then((r) => setMessages(r.data)).catch(onErr);
  }, [token, logout]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/availability`, availability, { headers: { Authorization: `Bearer ${token}` } });
      setAvailability(data);
      toast.success("Disponibilità salvata");
    } catch (e) {
      if (e.response?.status === 401) logout();
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const pending = bookings.filter((b) => (b.status || "pending") === "pending").length;

  return (
    <main className="pt-28 pb-24 sm:pt-36" data-testid="admin-page">
      <Seo title="Area di Giada" description="Pannello privato" noindex />
      <div className="max-w-4xl mx-auto px-5">
        {!token ? (
          <Reveal><AdminLogin onLogin={setToken} /></Reveal>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4 mb-8">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.22em] text-clay font-semibold mb-3">Area di Giada</p>
                <h1 className="font-serif font-semibold text-4xl text-ink tracking-tight" data-testid="admin-title">Il tuo pannello</h1>
              </div>
              <button type="button" onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm text-muted hover:text-ink"><LogOut size={15} /> Esci</button>
            </div>

            <div className="flex gap-2 mb-8 flex-wrap" data-testid="admin-tabs">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  data-testid={`admin-tab-${t.id}`}
                  className={`rounded-full px-5 py-2 text-sm font-medium transition-colors duration-300 ${tab === t.id ? "bg-pine text-linen" : "bg-white border border-border text-ink/70 hover:border-pine/50"}`}
                >
                  {t.label}
                  {t.id === "bookings" && pending > 0 && <span className="ml-2 rounded-full bg-clay px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid="pending-count">{pending}</span>}
                  {t.id === "messages" && messages.length > 0 && <span className="ml-2 text-xs opacity-70">{messages.length}</span>}
                </button>
              ))}
            </div>

            {tab === "bookings" && (
              <section data-testid="admin-bookings">
                <p className="text-sm text-muted mb-4">Conferma o rifiuta: il cliente riceve subito un'email nella sua lingua. Il giorno prima della consulenza confermata parte un promemoria automatico.</p>
                <BookingList bookings={bookings} token={token} onUpdate={(u) => setBookings(bookings.map((b) => (b.id === u.id ? u : b)))} />
              </section>
            )}

            {tab === "messages" && (
              <section data-testid="admin-messages">
                <MessageList messages={messages} />
              </section>
            )}

            {tab === "blog" && (
              <section data-testid="admin-blog">
                <BlogAdmin token={token} onUnauthorized={logout} />
              </section>
            )}

            {tab === "availability" && availability && (
              <div className="space-y-10" data-testid="admin-availability">
                <section data-testid="admin-weekly">
                  <h2 className="font-serif text-2xl text-ink mb-1">Orari settimanali</h2>
                  <p className="text-sm text-muted mb-4">Gli orari che aggiungi qui sono quelli che i clienti vedono su /prenota.</p>
                  <WeeklyEditor weekly={availability.weekly} onChange={(weekly) => setAvailability({ ...availability, weekly })} />
                </section>
                <section data-testid="admin-blocked">
                  <h2 className="font-serif text-2xl text-ink mb-4">Giorni bloccati</h2>
                  <BlockedDates dates={availability.blocked_dates} onChange={(blocked_dates) => setAvailability({ ...availability, blocked_dates })} />
                </section>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  data-testid="admin-save"
                  className="inline-flex items-center gap-2 rounded-full bg-clay px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-clay-dark disabled:opacity-50"
                >
                  <Save size={16} /> {saving ? "Salvataggio..." : "Salva disponibilità"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
